import {useEffect, useRef, useState} from 'react';
import {Loader2, Mail, Power, RefreshCw, Save} from 'lucide-react';
import {STALE_TIME_30_SEC} from '@common/constants/time';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@common-web/components/ui/sheet';
import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {ReadOnlyField} from '@client/components/InfoFields';
import {StateSelect} from '@client/components/StateSelect';

/** Identifies a VIP row clicked in the member search; null when the panel is closed. */
export type QuickViewVip = {
  clientId: string;
  uniqueId: string;
  firstName: string | null;
  lastName: string | null;
};

type VipType = 'broker' | 'owner' | 'team' | 'other';

const VIP_TYPE_LABELS: Record<VipType, string> = {
  broker: 'Broker',
  owner: 'Owner',
  team: 'Team',
  other: 'Other',
};

const EMPTY_EDIT = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  gender: '' as '' | 'M' | 'F' | 'O',
  dateOfBirth: '',
  vipType: '' as '' | VipType,
  vipBrokerage: '',
};

function StatusBadge({isActive}: {isActive: boolean}) {
  return isActive ? (
    <Badge className='shrink-0 bg-emerald text-white border-0 font-semibold'>Active</Badge>
  ) : (
    <Badge className='shrink-0 bg-brand-gray text-white border-0 font-semibold'>Inactive</Badge>
  );
}

interface VipQuickViewSheetProps {
  vip: QuickViewVip | null;
  onOpenChange: (open: boolean) => void;
}

export function VipQuickViewSheet({vip, onOpenChange}: VipQuickViewSheetProps) {
  // Keep the last VIP rendered so the slide-out close animation doesn't blank the panel.
  const lastVipRef = useRef<QuickViewVip | null>(null);
  if (vip) lastVipRef.current = vip;
  const displayVip = vip ?? lastVipRef.current;

  const utils = trpc.useUtils();
  const isInternal = useIsInternal();

  const recordQuery = trpc.vip.get.useQuery(
    {clientId: displayVip?.clientId ?? '', uniqueId: displayVip?.uniqueId ?? ''},
    {enabled: !!vip, staleTime: STALE_TIME_30_SEC},
  );
  const record = recordQuery.data;

  const contextQuery = trpc.vip.context.useQuery(
    {clientId: displayVip?.clientId},
    {enabled: !!vip, staleTime: STALE_TIME_30_SEC},
  );
  const vipTypeOptions = contextQuery.data?.vipTypeOptions ?? [];
  // Tenants without VIP-type options (e.g. Synrgy) don't edit type/brokerage.
  const usesVipType = vipTypeOptions.length > 0;

  const [edit, setEdit] = useState(EMPTY_EDIT);

  // Reseed the editable form whenever a fresh record loads.
  useEffect(() => {
    if (!record) return;
    setEdit({
      firstName: record.firstName ?? '',
      lastName: record.lastName ?? '',
      email: record.email ?? '',
      phoneNumber: record.phoneNumber ?? '',
      address1: record.address1 ?? '',
      address2: record.address2 ?? '',
      city: record.city ?? '',
      state: record.state ?? '',
      zipCode: record.zipCode ?? '',
      gender: (record.gender as '' | 'M' | 'F' | 'O') ?? '',
      dateOfBirth: record.dateOfBirth ?? '',
      vipType: (record.vipType as '' | VipType) ?? '',
      vipBrokerage: record.vipBrokerage ?? '',
    });
  }, [record]);

  const invalidate = () => {
    void utils.vip.get.invalidate();
    void utils.vba.members.search.invalidate();
  };

  const updateMutation = trpc.vip.update.useMutation({
    onError: createMutationErrorHandler('Error updating VIP member'),
  });

  const sendWelcomeEmail = trpc.vip.sendWelcomeEmail.useMutation({
    onSuccess: ({to}) => {
      toast({title: 'Welcome email sent', description: `A welcome message was sent to ${to}.`});
    },
    onError: createMutationErrorHandler('Error sending welcome email'),
  });

  const setField = (patch: Partial<typeof EMPTY_EDIT>) => setEdit((prev) => ({...prev, ...patch}));

  const canSave =
    !!record &&
    !!edit.firstName.trim() &&
    !!edit.lastName.trim() &&
    !!edit.state &&
    /^\d{5}$/.test(edit.zipCode) &&
    !!edit.gender &&
    (!usesVipType || (!!edit.vipType && !!edit.vipBrokerage.trim()));

  const handleSave = () => {
    if (!displayVip || !canSave || !edit.gender) return;
    if (usesVipType && !edit.vipType) return;
    updateMutation.mutate(
      {
        clientId: displayVip.clientId,
        uniqueId: displayVip.uniqueId,
        patch: {
          firstName: edit.firstName.trim(),
          lastName: edit.lastName.trim(),
          email: edit.email.trim(),
          phoneNumber: edit.phoneNumber.replace(/\D/g, ''),
          address1: edit.address1,
          address2: edit.address2,
          city: edit.city,
          state: edit.state,
          zipCode: edit.zipCode.replace(/\D/g, ''),
          gender: edit.gender,
          // DOB can't be cleared, only changed; skip the patch field when the input is empty.
          ...(edit.dateOfBirth ? {dateOfBirth: edit.dateOfBirth} : {}),
          ...(usesVipType && edit.vipType
            ? {vipType: edit.vipType, vipBrokerage: edit.vipBrokerage.trim()}
            : {}),
        },
      },
      {
        onSuccess: () => {
          toast({title: 'VIP member updated'});
          invalidate();
        },
      },
    );
  };

  const handleToggleActive = () => {
    if (!displayVip || !record) return;
    updateMutation.mutate(
      {
        clientId: displayVip.clientId,
        uniqueId: displayVip.uniqueId,
        patch: {isActive: !record.isActive},
      },
      {
        onSuccess: () => {
          toast({title: record.isActive ? 'VIP member deactivated' : 'VIP member reactivated'});
          invalidate();
        },
      },
    );
  };

  const headerName =
    [record?.lastName ?? displayVip?.lastName, record?.firstName ?? displayVip?.firstName]
      .filter(Boolean)
      .join(', ') || 'VIP member';

  const appAccountStatus = record?.appUserId ? 'Set up' : 'Not yet';
  const appEmailVerified = record?.appUserId && record.appUserEmailVerified ? 'Yes' : 'Not yet';

  return (
    <Sheet open={!!vip} onOpenChange={onOpenChange}>
      <SheetContent side='right' className='flex w-full flex-col gap-0 p-0 sm:max-w-xl'>
        <SheetHeader className='border-b border-gray-100 px-6 py-4 pr-12 text-left'>
          <div className='flex items-center gap-3'>
            <SheetTitle className='text-brand-navy'>{headerName}</SheetTitle>
            <Badge className='shrink-0 bg-amber-500 text-white border-0 font-semibold'>VIP</Badge>
            {record && <StatusBadge isActive={record.isActive} />}
          </div>
          <SheetDescription>
            {[record?.groupName, displayVip ? `ID ${displayVip.uniqueId}` : null]
              .filter(Boolean)
              .join(' · ')}
          </SheetDescription>
        </SheetHeader>

        <div className='flex-1 space-y-6 overflow-y-auto px-6 py-5'>
          {recordQuery.error ? (
            <div className='flex flex-col items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3'>
              <p className='text-sm text-red-600'>
                {recordQuery.error.data?.code === 'BAD_REQUEST' && recordQuery.error.message
                  ? recordQuery.error.message
                  : 'Failed to load VIP member'}
              </p>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => void recordQuery.refetch()}
              >
                <RefreshCw className='mr-2 h-4 w-4' />
                Retry
              </Button>
            </div>
          ) : !record ? (
            <div className='space-y-3'>
              {Array.from({length: 6}, (_, i) => (
                <Skeleton key={i} className='h-9 w-full' />
              ))}
            </div>
          ) : (
            <>
              <section className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-2'>
                  <Label htmlFor='vip-edit-first'>First name</Label>
                  <Input
                    id='vip-edit-first'
                    value={edit.firstName}
                    onChange={(e) => setField({firstName: e.target.value})}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='vip-edit-last'>Last name</Label>
                  <Input
                    id='vip-edit-last'
                    value={edit.lastName}
                    onChange={(e) => setField({lastName: e.target.value})}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='vip-edit-email'>Email</Label>
                  <Input
                    id='vip-edit-email'
                    type='email'
                    value={edit.email}
                    onChange={(e) => setField({email: e.target.value})}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='vip-edit-phone'>Phone</Label>
                  <Input
                    id='vip-edit-phone'
                    value={edit.phoneNumber}
                    onChange={(e) => setField({phoneNumber: e.target.value})}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='vip-edit-gender'>Gender</Label>
                  <Select
                    value={edit.gender || undefined}
                    onValueChange={(v) => setField({gender: v as 'M' | 'F' | 'O'})}
                  >
                    <SelectTrigger id='vip-edit-gender'>
                      <SelectValue placeholder='Select gender' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='M'>Male</SelectItem>
                      <SelectItem value='F'>Female</SelectItem>
                      <SelectItem value='O'>Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='vip-edit-dob'>Date of birth</Label>
                  <Input
                    id='vip-edit-dob'
                    type='date'
                    value={edit.dateOfBirth}
                    onChange={(e) => setField({dateOfBirth: e.target.value})}
                  />
                </div>
              </section>

              <section className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='vip-edit-addr1'>Address</Label>
                  <Input
                    id='vip-edit-addr1'
                    value={edit.address1}
                    onChange={(e) => setField({address1: e.target.value})}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='vip-edit-addr2'>Address line 2</Label>
                  <Input
                    id='vip-edit-addr2'
                    value={edit.address2}
                    onChange={(e) => setField({address2: e.target.value})}
                  />
                </div>
                <div className='grid grid-cols-3 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='vip-edit-city'>City</Label>
                    <Input
                      id='vip-edit-city'
                      value={edit.city}
                      onChange={(e) => setField({city: e.target.value})}
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='vip-edit-state'>State</Label>
                    <StateSelect value={edit.state} onValueChange={(v) => setField({state: v})} />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='vip-edit-zip'>ZIP</Label>
                    <Input
                      id='vip-edit-zip'
                      value={edit.zipCode}
                      onChange={(e) => setField({zipCode: e.target.value})}
                    />
                  </div>
                </div>
              </section>

              {usesVipType && (
                <section className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                  <div className='space-y-2'>
                    <Label htmlFor='vip-edit-type'>VIP type</Label>
                    <Select
                      value={edit.vipType || undefined}
                      onValueChange={(v) => setField({vipType: v as VipType})}
                    >
                      <SelectTrigger id='vip-edit-type'>
                        <SelectValue placeholder='Select VIP type' />
                      </SelectTrigger>
                      <SelectContent>
                        {vipTypeOptions.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {VIP_TYPE_LABELS[opt]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='vip-edit-brokerage'>Group / Brokerage</Label>
                    <Input
                      id='vip-edit-brokerage'
                      value={edit.vipBrokerage}
                      onChange={(e) => setField({vipBrokerage: e.target.value})}
                    />
                  </div>
                </section>
              )}

              <section className='space-y-3 border-t border-gray-100 pt-5'>
                <h3 className='text-sm font-semibold text-brand-navy'>App account</h3>
                <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
                  <ReadOnlyField label='Status' value={appAccountStatus} />
                  <ReadOnlyField label='Login email' value={record.appUserEmail} />
                  <ReadOnlyField label='Email verified' value={appEmailVerified} />
                </div>
              </section>
            </>
          )}
        </div>

        <SheetFooter className='flex-col gap-2 border-t border-gray-100 px-6 py-4 sm:flex-row sm:justify-between'>
          <div className='flex gap-2'>
            <Button
              type='button'
              variant='outline'
              onClick={handleToggleActive}
              disabled={!record || updateMutation.isPending}
            >
              <Power className='mr-2 h-4 w-4' />
              {record?.isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
            {!isInternal && (
              <Button
                type='button'
                variant='outline'
                onClick={() =>
                  displayVip &&
                  sendWelcomeEmail.mutate({
                    clientId: displayVip.clientId,
                    uniqueId: displayVip.uniqueId,
                  })
                }
                disabled={
                  !record ||
                  !record.welcomeEmailSupported ||
                  !record.email ||
                  sendWelcomeEmail.isPending
                }
                title={
                  !record?.welcomeEmailSupported
                    ? 'Welcome emails are not configured for this client'
                    : !record?.email
                      ? 'This VIP has no email on file'
                      : 'Send welcome email'
                }
              >
                {sendWelcomeEmail.isPending ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : (
                  <Mail className='mr-2 h-4 w-4' />
                )}
                Welcome email
              </Button>
            )}
          </div>
          <Button type='button' onClick={handleSave} disabled={!canSave || updateMutation.isPending}>
            {updateMutation.isPending ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <Save className='mr-2 h-4 w-4' />
            )}
            Save changes
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
